import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { sendTemplateEmail } from "@/lib/email-templates/send-email";

const ContactSchema = z.object({
  name: z.string().trim().max(120).optional().default(""),
  email: z.string().trim().email().max(254),
  message: z.string().trim().max(2000).optional().default(""),
  source: z.string().trim().max(80).optional().default("homepage-contact"),
});

export const submitContact = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => ContactSchema.parse(input))
  .handler(async ({ data }) => {
    const url = process.env.SUPABASE_URL!;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
    const supabase = createClient<Database>(url, key, {
      auth: { persistSession: false, autoRefreshToken: false },
    });

    // No .select() chain: the anon INSERT policy allows writes but there is
    // no SELECT policy on leads, so returning the row triggers a 42501.
    const insertResult = await supabase
      .from("leads")
      .insert({
        name: data.name || null,
        email: data.email,
        message: data.message || null,
        source: data.source,
      });

    if (insertResult.error) {
      console.error("contact insert failed", insertResult.error);
      throw new Error("Could not send message. Please try again or email katie@katiefernands.com directly.");
    }

    const leadId = crypto.randomUUID();
    const displayName = data.name || data.email;

    // Fire-and-forget notifications. Never block or fail the submission.
    void sendTemplateEmail("lead-notification", "kfernands1@gmail.com", {
      templateData: {
        name: displayName,
        email: data.email,
        message: data.message || "(no message)",
      },
      idempotencyKey: `lead-notification-${leadId}`,
      replyTo: data.email,
    }).catch((err) => console.warn("lead notification email failed", err));

    void sendTemplateEmail("contact-confirmation", data.email, {
      templateData: { name: data.name || "there" },
      idempotencyKey: `contact-confirmation-${leadId}`,
      from: "Katie Fernands <katie@katiefernands.com>",
      replyTo: "katie@katiefernands.com",
    }).catch((err) => console.warn("visitor confirmation email failed", err));

    return { ok: true as const };
  });
